import { useContext, useMemo } from 'react';
import { HomeOutlined, Settings, CasesOutlined } from '@mui/icons-material';
import { Box, Button, styled, alpha } from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/context/SidebarContext';
import pages from '../../../../router/routes';
import { AbilityGuard } from 'src/Guards/abilityGuard/AbilityGuard';
import { ABILITY_PAGES, ABILITY_TYPES } from 'src/config/ability';
import { env } from 'src/env';

const MenuWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const MenuButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.sidebar.menuItemColor,
  width: '100%',
  justifyContent: 'flex-start',
  fontWeight: 600,
  fontSize: theme.typography.pxToRem(14),
  textTransform: 'none',
  textAlign: 'left',
  textDecoration: 'none',
  transition: theme.transitions.create(['background-color', 'color', 'transform'], {
    duration: 200,
  }),
  minHeight: 48,
  position: 'relative',

  '& .MuiButton-startIcon': {
    color: theme.sidebar.menuItemIconColor,
    fontSize: theme.typography.pxToRem(22),
    marginLeft: theme.spacing(0.5),
    minWidth: theme.typography.pxToRem(22),
    transition: theme.transitions.create(['color', 'transform'], { duration: 200 }),
  },

  '& .MuiButton-label, & > span:not(.MuiButton-startIcon)': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    textAlign: 'left',
    lineHeight: 1.2,
  },

  '&.active': {
    backgroundColor: alpha(theme.colors.primary.dark, 0.12),
    color: theme.colors.primary.dark,
    fontWeight: 700,

    '& .MuiButton-startIcon': {
      color: theme.colors.primary.dark,
      transform: 'scale(1.1)',
    },
  },

  '&:hover:not(.active)': {
    backgroundColor: alpha(theme.sidebar.menuItemBgActive || theme.colors.alpha.black[100], 0.06),
    color: theme.sidebar.menuItemColorActive,
    transform: 'translateX(2px)',

    '& .MuiButton-startIcon': {
      color: theme.sidebar.menuItemIconColorActive,
      transform: 'scale(1.05)',
    },
  },

  '&:active': {
    transform: 'translateX(1px) scale(0.98)',
  },
})) as typeof Button;

const menuItems = [
  {
    key: 'disclosure',
    label: 'Disclosure List',
    icon: HomeOutlined,
    path: '/home',
    ability: { type: ABILITY_TYPES.VISIT, page: ABILITY_PAGES.HOME },
  },
  {
    key: 'cases',
    label: 'Case List',
    icon: CasesOutlined,
    path: pages.cases.root,
    ability: { type: ABILITY_TYPES.VISIT, page: ABILITY_PAGES.HOME },
    devOnly: false,
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: Settings,
    path: pages.settings.path,
    ability: { type: ABILITY_TYPES.VISIT, page: ABILITY_PAGES.HOME },
  },
];

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);
  const filteredMenuItems = useMemo(() => {
    return menuItems.filter((item) => {
      if (item.devOnly && env.REACT_APP_DEPLOYMENT_ENV !== 'DEV') {
        return false;
      }
      return true;
    });
  }, []);

  return (
    <MenuWrapper>
      {filteredMenuItems.map(({ key, label, icon: Icon, path, ability }) => (
        <AbilityGuard key={key} i={ability.type} a={ability.page}>
          <MenuButton disableRipple component={RouterLink} onClick={closeSidebar} to={path} startIcon={<Icon />}>
            {label}
          </MenuButton>
        </AbilityGuard>
      ))}
    </MenuWrapper>
  );
}

export default SidebarMenu;
